import React, { useState } from 'react';
import { CheckCircleIcon, KeyIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { engineerService } from '../../services/engineerService';
import { PageHeader, Button, FormField, TextInput } from '../../components/ui';

const EngineerSettings: React.FC = () => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSaving, setPinSaving] = useState(false);

  const changePin = async () => {
    setPinError(null);
    setPinMessage(null);
    if (!/^\d{6}$/.test(newPin)) {
      setPinError('New PIN must be 6 digits.');
      return;
    }
    if (newPin !== confirmPin) {
      setPinError('PINs do not match.');
      return;
    }
    setPinSaving(true);
    try {
      await engineerService.changePin({ currentPin: currentPin || undefined, newPin });
      setPinMessage('PIN updated');
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
    } catch (err: any) {
      setPinError(err?.response?.data?.message || err?.message || 'Failed to change PIN');
    } finally {
      setPinSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-6">
      <PageHeader
        title="Settings"
        subtitle="Sign-in PIN, notification preferences and app options."
      />

      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <KeyIcon className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-900">Sign-in PIN</h2>
        </div>
        <p className="text-xs text-slate-500">
          Change your 6-digit PIN used to sign into the engineer app.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <FormField label="Current PIN">
            <TextInput
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              type="password"
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
          <FormField label="New PIN">
            <TextInput
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
          <FormField label="Confirm new PIN">
            <TextInput
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              type="password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
        </div>
        <div className="flex items-center justify-end gap-2">
          {pinMessage && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircleIcon className="w-4 h-4" /> {pinMessage}
            </span>
          )}
          {pinError && <span className="text-xs text-red-700">{pinError}</span>}
          <Button onClick={changePin} loading={pinSaving}>
            Update PIN
          </Button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <BellAlertIcon className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-900">Notifications</h2>
        </div>
        <p className="text-xs text-slate-500">
          Preference controls are coming soon. For now, all in-app notifications are
          delivered instantly and email alerts follow your admin's setup.
        </p>
      </section>
    </div>
  );
};

export default EngineerSettings;
